import React from 'react';
import styles from './header.module.css';
import { usePage } from '../../../context/PageContext';
import { pages } from '../../../context/pages';

/**
 * Presentational header. With `titleBar` it renders the packaging layout: an uppercase title bar,
 * then the heading. Without it, the original layout.
 * @param {{ titleBar?: string, heading?: string, description?: string, subDescription?: string, onBack?: (() => void) | null, onClose?: ((e?: any) => void) | null }} props
 * @returns {import('react').JSX.Element}
 */
export function HeaderView({ titleBar = '', heading = '', description = '', subDescription = '', onBack = null, onClose = null }) {
  return (
    <div className={`${styles['giftWrap__header']} ${titleBar ? styles['giftWrap__header--titlebar'] : ''}`}>
      <div className={styles['giftWrap__header-content']}>
        <div className={styles['giftWrap__header-title']}>
          {onBack && (
            <button
              className={styles['giftWrap__header-back'] + ' svg-wrapper button-reset'}
              onClick={onBack}
              aria-label="Go back"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M15.7071 5.29289C16.0976 5.68342 16.0976 6.31658 15.7071 6.70711L10.4142 12L15.7071 17.2929C16.0976 17.6834 16.0976 18.3166 15.7071 18.7071C15.3166 19.0976 14.6834 19.0976 14.2929 18.7071L8.29289 12.7071C7.90237 12.3166 7.90237 11.6834 8.29289 11.2929L14.2929 5.29289C14.6834 4.90237 15.3166 4.90237 15.7071 5.29289Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          )}
          {titleBar ? (
            <p className={`body-xs ${styles['giftWrap__header-titlebar']}`}>{titleBar}</p>
          ) : (
            heading && <h2 className="heading-2xl">{heading}</h2>
          )}
        </div>
        <div className={styles['giftWrap__header-actions']}>
          {onClose && (
            <button
              className={styles['giftWrap__header-close'] + ' svg-wrapper button-reset'}
              onClick={(e) => onClose(e)}
              aria-label="Close"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M5.35404 4.64645C5.15878 4.45118 4.8422 4.45118 4.64694 4.64645C4.45167 4.84171 4.45167 5.15829 4.64693 5.35355L11.2934 12L4.64693 18.6465C4.45167 18.8417 4.45167 19.1583 4.64694 19.3536C4.8422 19.5488 5.15878 19.5488 5.35404 19.3536L12.0005 12.7071L18.6469 19.3536C18.8422 19.5488 19.1588 19.5488 19.3541 19.3536C19.5493 19.1583 19.5493 18.8417 19.3541 18.6465L12.7076 12L19.3541 5.35355C19.5493 5.15829 19.5493 4.84171 19.3541 4.64645C19.1588 4.45118 18.8422 4.45118 18.6469 4.64645L12.0005 11.2929L5.35404 4.64645Z"
                  fill="#171717"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      {titleBar && heading && (
        <div className={styles['giftWrap__header-heading']}>
          <h2 className="heading-2xl">{heading}</h2>
        </div>
      )}
      {description && (
        <div className={styles['giftWrap__header-description']}>
          <p className="body-sm">{description}</p>
        </div>
      )}
      {subDescription && (
        <div className={styles['giftWrap__header-sub']}>
          <p className="body-sm">{subDescription}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Config-driven header for the old flow and the card builder pages.
 * @param {{ onClose: (e?: any) => void }} props
 * @returns {import('react').JSX.Element}
 */
export function Header({ onClose }) {
  const { currentPage, goToPage } = usePage();
  const pageConfig = pages[currentPage];
  const backTo = pageConfig?.backTo || 'firstPage';

  return (
    <HeaderView
      titleBar={pageConfig?.titleBar || ''}
      heading={pageConfig?.heading || ''}
      description={pageConfig?.description || ''}
      subDescription={pageConfig?.subDescription || ''}
      onBack={pageConfig?.hasBackButton ? () => goToPage(backTo) : null}
      onClose={onClose}
    />
  );
}
